import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, ScrollVFlex, Space, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Quote from '@/components/Quote/Quote';
import { DefaultTabItemStyle } from '@/components/Tab/TabHeader';
import Tabs from '@/components/Tab/Tabs';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useFilterItemsStore from '@/stores/FilterItemsStore';
import usePopupStore from '@/stores/pure/PopupStore';
import { ReanalyzeStatus } from '@/types/filterItems';

import SettingHeader from '../Header';
import FilterItemsAnalysisEmailTab from './FilterItemsAnalysisEmailTab';
import FilterItemsAnalysisTextTab from './FilterItemsAnalysisTextTab';

const TAB_TEXT_FILTER_ID = 'filter-items-analysis-text-filter';
const TAB_EMAIL_FILTER_ID = 'filter-items-analysis-email-filter';

const FilterItemsAnalysisBody = () => {
  const color = useColor();
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const { reanalyzeStatus, setReanalyzeStatus } = useFilterItemsStore();

  const [hasTextTabChange, setHasTextTabChange] = useState(false);
  const [hasEmailTabChange, setHasEmailTabChange] = useState(false);

  const { isLoading: isLoadingReanalyzeStatus } = useQuery({
    queryKey: queryKeys.filterItems.FILTER_ITEMS_REANALYZE,
    queryFn: async () => {
      const data = await service.filterItems.fetchAnalysisReanalyzeStatus();

      if (data) {
        setReanalyzeStatus(data);
      }

      return data;
    },
  });

  const editDisabled =
    reanalyzeStatus === ReanalyzeStatus.REANALYZE_STATUS_RUNNING;

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        id: TAB_TEXT_FILTER_ID,
        text: 'Text',
        content: (
          <FilterItemsAnalysisTextTab
            setHasTextTabChange={setHasTextTabChange}
          />
        ),
      },
      {
        id: TAB_EMAIL_FILTER_ID,
        text: `Email`,
        content: (
          <FilterItemsAnalysisEmailTab
            setHasEmailTabChange={setHasEmailTabChange}
          />
        ),
      },
    ];
  }, []);
  const [selectedTab, selectTab] = useTabs(tabs, TAB_EMAIL_FILTER_ID);

  const handleSwitchTab = (id: StringNumberValue | null) => {
    if (
      (selectedTab === TAB_TEXT_FILTER_ID && hasTextTabChange) ||
      (selectedTab === TAB_EMAIL_FILTER_ID && hasEmailTabChange)
    ) {
      setPopup({
        popupTheme: 'neutral',
        type: 'confirm',
        title: 'Save change',
        message: 'There are unsaved changes. Do you want to proceed?',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        onConfirm: () => {
          selectTab(id || null);
        },
      });

      return;
    }

    selectTab(id || null);
  };

  return (
    <VFlex height="100vh">
      <SettingHeader title="Filter items: Analysis" />
      <ScrollVFlex padding="32px" f={1}>
        <Quote
          themeColor={color.red600}
          icon={<WarningOutlined />}
          title="Note"
          style={{
            fontSize: 12,
            lineHeight: 1.6,
          }}
        >
          <ul style={{ paddingLeft: 24 }}>
            <li>
              Items on the list will be excluded from AI analysis including
              sentiment and insight analyses.
            </li>
            <li>
              Addition or deletion of items on the list will trigger re-analysis
              of your data which may take a few minutes.
            </li>
          </ul>
        </Quote>

        <Space h={36} />

        {editDisabled && (
          <>
            <VFlex
              padding="16px"
              style={{
                backgroundColor: color.yellow(0.15),
                borderRadius: '10px',
                border: `1px solid ${color.gray300}`,
              }}
              g={16}
            >
              <Flex g={8}>
                <Spinner size={12} />
                <Typo type="label3" weight="b" className="text-gray-700">
                  AI analysis in progress...
                </Typo>
              </Flex>

              <Typo type="body3" className="text-gray-800">
                We are re-analyzing your data based on the changes you made. You
                cannot make any further changes during this process.
              </Typo>
            </VFlex>
            <Space h={36} />
          </>
        )}

        <Transition
          status={!isLoadingReanalyzeStatus}
          fallback={
            <CenterFlex style={{ height: '400px' }}>
              <Spinner />
            </CenterFlex>
          }
        >
          <Tabs tabs={tabs} selectedTabId={selectedTab}>
            <Flex>
              <Tabs.TabHeader
                scrollEnabled={false}
                tabSize="m"
                tabColor="primary"
                tabStyle={StyledCustomTab}
                onSelect={handleSwitchTab}
              />
            </Flex>

            <Tabs.TabBody className="flex-col" />
          </Tabs>
        </Transition>
      </ScrollVFlex>
    </VFlex>
  );
};

export default FilterItemsAnalysisBody;

const StyledCustomTab = styled(DefaultTabItemStyle)`
  width: 200px;
  padding: 16px 10px;
`;
